import { FormControlLabel } from '@mui/material';

import {
  Box,
  Divider,
  Skeleton,
  Stack,
  tokens,
  Typography,
} from '@cast/design-system';

import { RffInput, RffSwitch, RffTimeInput } from '@cast/shared-rff-ui';

import { FIELD_NAMES } from '../_constants';

import { CAST_AI } from '@cast/constants';

const Layout = ({
  left,
  right,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
}) => {
  return (
    <Stack
      direction="row"
      gap={24}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <Stack gap={16} width="50%">
        {left}
      </Stack>
      <Stack gap={16} width="50%">
        {right}
      </Stack>
    </Stack>
  );
};

type SettingLabelProps = {
  title: string;
  description: string;
};

const SettingLabel = ({ title, description }: SettingLabelProps) => (
  <Stack gap={2}>
    <Typography variant="A1">{title}</Typography>
    <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
      {description}
    </Typography>
  </Stack>
);

type SettingProps = SettingLabelProps & {
  input: React.ReactElement;
};

const Setting = ({ title, description, input }: SettingProps) => (
  <Stack
    direction="row"
    gap={24}
    alignItems="flex-start"
    justifyContent="space-between"
  >
    <SettingLabel title={title} description={description} />
    <Box sx={{ width: 160 }}>{input}</Box>
  </Stack>
);

type ToggleSettingProps = SettingLabelProps & {
  control: React.ReactElement;
};

const ToggleSetting = ({ title, description, control }: ToggleSettingProps) => (
  <FormControlLabel
    sx={{
      m: 0,
      gap: 40,
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    }}
    labelPlacement="start"
    control={control}
    label={<SettingLabel title={title} description={description} />}
  />
);

export const SettingsSection = () => {
  return (
    <Layout
      left={
        <>
          <ToggleSetting
            control={
              <RffSwitch
                size="sm"
                name={FIELD_NAMES.aggressiveMode}
                testId="evictor-aggressive-mode"
              />
            }
            title="Aggressive mode"
            description="Evicts single-replica deployments and jobs. Not recommended for production clusters."
          />
          <ToggleSetting
            control={
              <RffSwitch
                size="sm"
                name={FIELD_NAMES.scopedMode}
                testId="evictor-scoped-mode"
              />
            }
            title="Scoped mode"
            description={`Only evict nodes provisioned by ${CAST_AI}. Customer-managed and cloud provider nodes will not be affected.`}
          />
          <ToggleSetting
            control={
              <RffSwitch
                size="sm"
                name={FIELD_NAMES.ignorePodDisruptionBudgets}
                testId="evictor-ignore-pdb"
              />
            }
            title="Ignore Pod Disruption Budgets"
            description="Override PDB constraints during eviction. Use with caution."
          />
        </>
      }
      right={
        <>
          <Setting
            title="Node grace period"
            description="Minimum time a node must exist before being considered for eviction."
            input={
              <RffTimeInput
                name={FIELD_NAMES.nodeGracePeriodMinutes}
                testId="evictor-node-grace-period"
                timeUnit="minute"
                // allowing only minutes because API accepts only integer values and expects minutes
                allowedTimeUnits={['minute']}
              />
            }
          />
          <Setting
            title="Max nodes per cycle"
            description="Maximum number of nodes that can be processed in a single evaluation cycle."
            input={
              <RffInput
                name={FIELD_NAMES.maxTargetNodesPerCycle}
                testId="evictor-max-nodes-per-cycle"
                type="number"
                min={1}
                step={1}
              />
            }
          />
          <Setting
            title="Cycle interval"
            description="How frequently the cluster is evaluated for optimization opportunities."
            input={
              <RffTimeInput
                name={FIELD_NAMES.cycleIntervalSeconds}
                testId="evictor-cycle-interval"
                timeUnit="second"
                allowedTimeUnits={['second', 'minute', 'hour']}
              />
            }
          />
        </>
      }
    />
  );
};

const SettingsSectionSkeleton = () => {
  return (
    <Layout
      left={<Skeleton height={180} />}
      right={<Skeleton height={180} />}
    />
  );
};

SettingsSection.Skeleton = SettingsSectionSkeleton;
